// src/pages/Notifications.js
import React, { useState, useEffect } from 'react';
import { Container, Card, Form, Button, Row, Col, Alert, Spinner } from 'react-bootstrap';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { notificationService } from '../api/services/notifications';

const Notifications = () => {
    const [isTestingSent, setIsTestingSent] = useState(false);
    const [testChannel, setTestChannel] = useState(null);

    const queryClient = useQueryClient();

    // Fetch notification settings - opraveno pro React Query v5
    const { data: settings, isLoading, error } = useQuery({
        queryKey: ['notificationSettings'],
        queryFn: () => notificationService.getSettings().then(res => res.data)
    });

    // Update settings mutation
    const updateSettingsMutation = useMutation({
        mutationFn: (updatedSettings) => notificationService.updateSettings(updatedSettings),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['notificationSettings'] });
        }
    });

    // Test notification mutation
    const testNotificationMutation = useMutation({
        mutationFn: (channel) => notificationService.testNotification(channel),
        onSettled: () => {
            setIsTestingSent(false);
            setTestChannel(null);
        }
    });

    const [formData, setFormData] = useState({
        email: {
            enabled: false,
            address: '',
            downtime: true,
            security: true,
            updates: true,
            performance: false,
            seo: false
        },
        sms: {
            enabled: false,
            number: '',
            downtime: true,
            security: false,
            updates: false
        },
        slack: {
            enabled: false,
            webhook: '',
            downtime: true,
            security: true,
            updates: true,
            performance: true,
            seo: true
        }
    });

    // Update form data when settings are loaded
    useEffect(() => {
        if (settings) {
            setFormData(settings);
        }
    }, [settings]);

    const handleChange = (channel, field, value) => {
        setFormData(prev => ({
            ...prev,
            [channel]: {
                ...prev[channel],
                [field]: value
            }
        }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        updateSettingsMutation.mutate(formData);
    };

    const handleTestNotification = (channel) => {
        setIsTestingSent(true);
        setTestChannel(channel);
        testNotificationMutation.mutate(channel);
    };

    if (isLoading) {
        return (
            <div className="text-center py-5">
                <Spinner animation="border" role="status" variant="primary">
                    <span className="visually-hidden">Načítání nastavení notifikací...</span>
                </Spinner>
            </div>
        );
    }

    if (error) {
        return <Alert variant="danger">Došlo k chybě při načítání nastavení notifikací.</Alert>;
    }

    return (
        <Container fluid className="">
            <h1 className="mb-4 ">Nastavení notifikací</h1>

            <Form onSubmit={handleSubmit}>
                {updateSettingsMutation.isSuccess && (
                    <Alert variant="success" dismissible className="">
                        <i className="fas fa-check-circle me-2"></i> Nastavení bylo úspěšně uloženo!
                    </Alert>
                )}

                {updateSettingsMutation.isError && (
                    <Alert variant="danger" dismissible className="">
                        <i className="fas fa-times-circle me-2"></i> Při ukládání došlo k chybě. Zkuste to prosím znovu.
                    </Alert>
                )}

                <Card className="mb-4 shadow-sm">
                    <Card.Header className="d-flex justify-content-between align-items-center">
                        <div className="d-flex align-items-center">
                            <i className="fas fa-envelope fa-lg me-3 text-primary"></i>
                            <h5 className="mb-0">Email</h5>
                        </div>
                        <Form.Check
                            type="switch"
                            id="email-toggle"
                            checked={formData.email?.enabled}
                            onChange={(e) => handleChange('email', 'enabled', e.target.checked)}
                            label=""
                        />
                    </Card.Header>

                    {formData.email?.enabled && (
                        <Card.Body>
                            <Row>
                                <Col lg={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Emailová adresa</Form.Label>
                                        <Form.Control
                                            type="email"
                                            value={formData.email?.address}
                                            onChange={(e) => handleChange('email', 'address', e.target.value)}
                                            placeholder="vas@email.cz"
                                        />
                                    </Form.Group>
                                </Col>
                            </Row>

                            <div className="mb-4">
                                <Form.Label>Typy upozornění</Form.Label>
                                <Row xs={1} md={2} lg={3}>
                                    <Col className="mb-2">
                                        <Form.Check
                                            type="checkbox"
                                            id="email-downtime"
                                            label="Výpadky"
                                            checked={formData.email?.downtime}
                                            onChange={(e) => handleChange('email', 'downtime', e.target.checked)}
                                        />
                                    </Col>
                                    <Col className="mb-2">
                                        <Form.Check
                                            type="checkbox"
                                            id="email-security"
                                            label="Bezpečnostní problémy"
                                            checked={formData.email?.security}
                                            onChange={(e) => handleChange('email', 'security', e.target.checked)}
                                        />
                                    </Col>
                                    <Col className="mb-2">
                                        <Form.Check
                                            type="checkbox"
                                            id="email-updates"
                                            label="Dostupné aktualizace"
                                            checked={formData.email?.updates}
                                            onChange={(e) => handleChange('email', 'updates', e.target.checked)}
                                        />
                                    </Col>
                                    <Col className="mb-2">
                                        <Form.Check
                                            type="checkbox"
                                            id="email-performance"
                                            label="Výkonnostní problémy"
                                            checked={formData.email?.performance}
                                            onChange={(e) => handleChange('email', 'performance', e.target.checked)}
                                        />
                                    </Col>
                                    <Col className="mb-2">
                                        <Form.Check
                                            type="checkbox"
                                            id="email-seo"
                                            label="SEO problémy"
                                            checked={formData.email?.seo}
                                            onChange={(e) => handleChange('email', 'seo', e.target.checked)}
                                        />
                                    </Col>
                                </Row>
                            </div>

                            <Button
                                variant="outline-primary"
                                size="sm"
                                onClick={() => handleTestNotification('email')}
                                disabled={isTestingSent || !formData.email?.address}
                            >
                                {isTestingSent && testChannel === 'email' ? (
                                    <>
                                        <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-2" />
                                        Odesílání...
                                    </>
                                ) : (
                                    <>
                                        <i className="fas fa-paper-plane me-2"></i> Odeslat testovací email
                                    </>
                                )}
                            </Button>
                        </Card.Body>
                    )}
                </Card>

                <Card className="mb-4 shadow-sm">
                    <Card.Header className="d-flex justify-content-between align-items-center">
                        <div className="d-flex align-items-center">
                            <i className="fas fa-sms fa-lg me-3 text-primary"></i>
                            <h5 className="mb-0">SMS</h5>
                        </div>
                        <Form.Check
                            type="switch"
                            id="sms-toggle"
                            checked={formData.sms?.enabled}
                            onChange={(e) => handleChange('sms', 'enabled', e.target.checked)}
                            label=""
                        />
                    </Card.Header>

                    {formData.sms?.enabled && (
                        <Card.Body>
                            <Row>
                                <Col lg={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Telefonní číslo</Form.Label>
                                        <Form.Control
                                            type="tel"
                                            value={formData.sms?.number}
                                            onChange={(e) => handleChange('sms', 'number', e.target.value)}
                                            placeholder="+420 123 456 789"
                                        />
                                    </Form.Group>
                                </Col>
                            </Row>

                            <div className="mb-4">
                                <Form.Label>Typy upozornění</Form.Label>
                                <Form.Text className="text-muted d-block mb-2">
                                    SMS jsou zpoplatněny podle aktuálního ceníku.
                                </Form.Text>
                                <Row xs={1} md={2} lg={3}>
                                    <Col className="mb-2">
                                        <Form.Check
                                            type="checkbox"
                                            id="sms-downtime"
                                            label="Výpadky"
                                            checked={formData.sms?.downtime}
                                            onChange={(e) => handleChange('sms', 'downtime', e.target.checked)}
                                        />
                                    </Col>
                                    <Col className="mb-2">
                                        <Form.Check
                                            type="checkbox"
                                            id="sms-security"
                                            label="Bezpečnostní problémy"
                                            checked={formData.sms?.security}
                                            onChange={(e) => handleChange('sms', 'security', e.target.checked)}
                                        />
                                    </Col>
                                    <Col className="mb-2">
                                        <Form.Check
                                            type="checkbox"
                                            id="sms-updates"
                                            label="Dostupné aktualizace"
                                            checked={formData.sms?.updates}
                                            onChange={(e) => handleChange('sms', 'updates', e.target.checked)}
                                        />
                                    </Col>
                                </Row>
                            </div>

                            <Button
                                variant="outline-primary"
                                size="sm"
                                onClick={() => handleTestNotification('sms')}
                                disabled={isTestingSent || !formData.sms?.number}
                            >
                                {isTestingSent && testChannel === 'sms' ? (
                                    <>
                                        <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-2" />
                                        Odesílání...
                                    </>
                                ) : (
                                    <>
                                        <i className="fas fa-paper-plane me-2"></i> Odeslat testovací SMS
                                    </>
                                )}
                            </Button>
                        </Card.Body>
                    )}
                </Card>

                <Card className="mb-4 shadow-sm">
                    <Card.Header className="d-flex justify-content-between align-items-center">
                        <div className="d-flex align-items-center">
                            <i className="fab fa-slack fa-lg me-3 text-primary"></i>
                            <h5 className="mb-0">Slack</h5>
                        </div>
                        <Form.Check
                            type="switch"
                            id="slack-toggle"
                            checked={formData.slack?.enabled}
                            onChange={(e) => handleChange('slack', 'enabled', e.target.checked)}
                            label=""
                        />
                    </Card.Header>

                    {formData.slack?.enabled && (
                        <Card.Body>
                            <Row>
                                <Col lg={8}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Webhook URL</Form.Label>
                                        <Form.Control
                                            type="text"
                                            value={formData.slack?.webhook}
                                            onChange={(e) => handleChange('slack', 'webhook', e.target.value)}
                                            placeholder="https://hooks.slack?.com/services/..."
                                        />
                                    </Form.Group>
                                </Col>
                            </Row>

                            <div className="mb-4">
                                <Form.Label>Typy upozornění</Form.Label>
                                <Row xs={1} md={2} lg={3}>
                                    <Col className="mb-2">
                                        <Form.Check
                                            type="checkbox"
                                            id="slack-downtime"
                                            label="Výpadky"
                                            checked={formData.slack?.downtime}
                                            onChange={(e) => handleChange('slack', 'downtime', e.target.checked)}
                                        />
                                    </Col>
                                    <Col className="mb-2">
                                        <Form.Check
                                            type="checkbox"
                                            id="slack-security"
                                            label="Bezpečnostní problémy"
                                            checked={formData.slack?.security}
                                            onChange={(e) => handleChange('slack', 'security', e.target.checked)}
                                        />
                                    </Col>
                                    <Col className="mb-2">
                                        <Form.Check
                                            type="checkbox"
                                            id="slack-updates"
                                            label="Dostupné aktualizace"
                                            checked={formData.slack?.updates}
                                            onChange={(e) => handleChange('slack', 'updates', e.target.checked)}
                                        />
                                    </Col>
                                    <Col className="mb-2">
                                        <Form.Check
                                            type="checkbox"
                                            id="slack-performance"
                                            label="Výkonnostní problémy"
                                            checked={formData.slack?.performance}
                                            onChange={(e) => handleChange('slack', 'performance', e.target.checked)}
                                        />
                                    </Col>
                                    <Col className="mb-2">
                                        <Form.Check
                                            type="checkbox"
                                            id="slack-seo"
                                            label="SEO problémy"
                                            checked={formData.slack?.seo}
                                            onChange={(e) => handleChange('slack', 'seo', e.target.checked)}
                                        />
                                    </Col>
                                </Row>
                            </div>

                            <Button
                                variant="outline-primary"
                                size="sm"
                                onClick={() => handleTestNotification('slack')}
                                disabled={isTestingSent || !formData.slack?.webhook}
                            >
                                {isTestingSent && testChannel === 'slack' ? (
                                    <>
                                        <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-2" />
                                        Odesílání...
                                    </>
                                ) : (
                                    <>
                                        <i className="fas fa-paper-plane me-2"></i> Odeslat testovací zprávu
                                    </>
                                )}
                            </Button>
                        </Card.Body>
                    )}
                </Card>

                <div className="d-flex justify-content-end mb-5">
                    <Button
                        type="submit"
                        variant="primary"
                        size="lg"
                        disabled={updateSettingsMutation.isPending}
                    >
                        {updateSettingsMutation.isPending ? (
                            <>
                                <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-2" />
                                Ukládání...
                            </>
                        ) : (
                            <>
                                <i className="fas fa-save me-2"></i> Uložit nastavení
                            </>
                        )}
                    </Button>
                </div>
            </Form>
        </Container>
    );
};

export default Notifications;